<template>
  <div
    v-if="preferredEmployees?.length > 0"
    class="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4"
  >
    <button
      type="button"
      @click="isAvailabilityExpanded = !isAvailabilityExpanded"
      class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
    >
      <ChevronDownIcon v-if="isAvailabilityExpanded" class="h-4 w-4" />
      <ChevronRightIcon v-else class="h-4 w-4" />
      <ChartBarIcon class="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
      Beschikbaarheid ({{ preferredEmployees.length }} medewerker{{
        preferredEmployees.length > 1 ? 's' : ''
      }})
    </button>

    <div v-if="isAvailabilityExpanded" class="space-y-4 mt-4">
      <EmployeeAvailabilityCard
        v-for="empId in preferredEmployees"
        :key="empId"
        :employee-id="empId"
        :employee-name="getEmployeeName(empId)"
        :employee="getEmployeeWithWeeks(empId)"
        :employee-info="getEmployeeInfo(empId)"
        :period-start="periodStart"
        :period-end="periodEnd"
        :potential-dpw="potentialDpw"
        :potential-label="potentialLabel"
        :is-expanded="isEmployeeGraphExpanded(empId)"
        @toggle="toggleEmployeeGraph(empId)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ChevronDownIcon, ChevronRightIcon, ChartBarIcon } from '@heroicons/vue/24/outline'
import EmployeeAvailabilityCard from '@/components/common/cards/EmployeeAvailabilityCard.vue'

const props = defineProps({
  preferredEmployees: {
    type: Array,
    default: () => []
  },
  employees: {
    type: Array,
    default: () => []
  },
  weeklyAvailability: {
    type: Object,
    default: () => ({})
  },
  periodStart: {
    type: String,
    default: null
  },
  periodEnd: {
    type: String,
    default: null
  },
  potentialDpw: {
    type: Number,
    default: 0
  },
  potentialLabel: {
    type: String,
    default: 'Huidige klus'
  }
})

const isAvailabilityExpanded = ref(true)

// Per-employee graph expansion state
const employeeGraphExpanded = ref({})

const toggleEmployeeGraph = empId => {
  // If undefined (default expanded), set to false to collapse
  const currentState = employeeGraphExpanded.value[empId] !== false
  employeeGraphExpanded.value[empId] = !currentState
}

const isEmployeeGraphExpanded = empId => {
  // Default to expanded (true)
  return employeeGraphExpanded.value[empId] !== false
}

// Helper functions for availability charts
const getEmployeeName = empId => {
  const emp = props.employees.find(e => e.simplicate_id === empId)
  return emp?.name || 'Onbekend'
}

const getEmployeeInfo = empId => {
  const emp = props.employees.find(e => e.simplicate_id === empId)
  if (!emp) return ''
  const parts = []
  if (emp.function) parts.push(emp.function)
  if (emp.teams?.length > 0) parts.push(emp.teams.map(t => t.name).join(', '))
  return parts.join(' • ')
}

const getEmployeeWithWeeks = empId => {
  const emp = props.employees.find(e => e.simplicate_id === empId)
  if (!emp) return null
  const weeks = props.weeklyAvailability[empId] || []
  if (weeks.length === 0) return null
  return {
    ...emp,
    weeks
  }
}
</script>
